import React, { useState, KeyboardEvent, ChangeEvent, useEffect, useImperativeHandle, forwardRef } from 'react';
import { IconCirclePlusFilled } from '@tabler/icons-react';
import { IoMdCloseCircle } from 'react-icons/io';

interface DestinatarisEditableProps {
  value?: string[];
  onChange?: (values: string[]) => void;
}

export interface DestinatarisEditableRef {
  hasUnsavedInput: () => boolean;
  getUnsavedInput: () => string;
}

const DestinatarisEditable = forwardRef<DestinatarisEditableRef, DestinatarisEditableProps>(
  ({ value = [], onChange }, ref) => {
    const [inputValue, setInputValue] = useState<string>('');
    const [destinataris, setDestinataris] = useState<string[]>(value);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>('');
    const [error, setError] = useState<string>('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Exponer métodos al componente padre
    useImperativeHandle(ref, () => ({
      hasUnsavedInput: () => inputValue.trim() !== '',
      getUnsavedInput: () => inputValue.trim()
    }));

    useEffect(() => {
      setDestinataris(value);
    }, [value]);

    useEffect(() => {
      if (onChange) {
        onChange(destinataris);
      }
    }, [destinataris, onChange]);

    const addDestinatari = (): void => {
      const trimmed = inputValue.trim();
      if (trimmed === '') return;

      if (!emailRegex.test(trimmed)) {
        setError('Introdueix un correu electrònic vàlid.');
        return;
      }

      setDestinataris([...destinataris, trimmed]);
      setInputValue('');
      setError('');
    };

    const removeDestinatari = (index: number): void => {
      setDestinataris(destinataris.filter((_, i) => i !== index));
    };

    const startEditing = (index: number, value: string): void => {
      setEditingIndex(index);
      setEditingValue(value);
    };

    const saveEditing = (): void => {
      if (editingValue.trim() === '' || editingIndex === null) {
        setEditingIndex(null);
        return;
      }

      if (!emailRegex.test(editingValue.trim())) {
        setError('Introdueix un correu electrònic vàlid.');
        return;
      }

      const updated = [...destinataris];
      updated[editingIndex] = editingValue.trim();
      setDestinataris(updated);
      setEditingIndex(null);
      setEditingValue('');
      setError('');
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        if (editingIndex !== null) {
          saveEditing();
        } else {
          addDestinatari();
        }
      }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
      setInputValue(e.target.value);
      setError('');
    };

    const handleEditChange = (e: ChangeEvent<HTMLInputElement>): void => {
      setEditingValue(e.target.value);
      setError('');
    };

    return (
      <div style={{ flex: 1 }}>
        <label style={{ fontWeight: 600 }}>
          Si s'escau, agregar altres destinataris a la resposta
        </label>
        <p style={{ fontSize: 12, color: '#666' }}>(no incloure el propi usuari)</p>

        {/* Campo de entrada */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
          <div style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="Afegeix destinatari..."
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: `1px solid ${error ? '#a00' : '#4385a6'}`,
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <IconCirclePlusFilled
            style={{ cursor: 'pointer', flexShrink: 0 }}
            size={26}
            onClick={addDestinatari}
          />
        </div>
        {error && (
          <p style={{ color: '#a00', fontSize: '12px', marginTop: '4px' }}>{error}</p>
        )}

        {/* Lista de destinatarios */}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {destinataris.map((d: string, i: number) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                width: '100%',
                marginTop: '6px',
              }}
            >
              <li
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#f2f2f2',
                  border: '1px solid #4385a6',
                  padding: '6px 10px',
                  borderRadius: '8px',
                  flex: 1,
                }}
              >
                {editingIndex === i ? (
                  <input
                    type="text"
                    value={editingValue}
                    onChange={handleEditChange}
                    onBlur={saveEditing}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    style={{
                      marginTop: '2px',
                      flex: 1,
                      padding: '4px 6px',
                      borderRadius: '6px',
                      border: '1px solid #4385a6',
                    }}
                  />
                ) : (
                  <span
                    onDoubleClick={() => startEditing(i, d)}
                    title="Doble clic per editar"
                    style={{ flex: 1, cursor: 'pointer' }}
                  >
                    {d}
                  </span>
                )}
              </li>
              <IoMdCloseCircle
                style={{ cursor: 'pointer', color: '#a00' }}
                size={26}
                onClick={() => removeDestinatari(i)}
              />
            </div>
          ))}
        </ul>
      </div>
    );
  }
);

DestinatarisEditable.displayName = 'DestinatarisEditable';

export default DestinatarisEditable;